/* Copyright (c) 2017 Backpack Health LLC */

import React from 'react';
import { FormattedMessage } from 'react-intl';

import { SecondaryButton } from '../../../components/buttons/Buttons';
import { TextLink } from '../../../components/TextLink';
import { Small, BodyMain, Heading } from '../../../components/Text';

import { isWholeNumber } from '../../../../utilities/Validators';

import Spacer from '../../../components/Spacer';

import ContentLoader from '../../../components/ContentLoader';
import InfoModal from '../../../components/modal/InfoModal';
import { NumberInput } from '../../../components/TextInput';
import ShareCardModel from '../../../../models/ShareCardModel';
import { useActiveUser } from '../../../hooks/useUser';
import { useShareCardForActiveProfile } from '../../../hooks/useShareCard';
import { useActiveProfile } from '../../../hooks/useProfile';

function generateNewPasscode(length, shareCard) {
  const { passcode: prevPassCode } = shareCard || { passcode: '' };
  const bound = 10 ** length;
  const rand = (Math.floor(Math.random() * bound) + bound);
  const passcode = rand.toString().substring(1);

  if (prevPassCode !== passcode) return passcode;
  return generateNewPasscode(length, shareCard);
}

interface Props {
  shareCard: any;
  goBackToEdit(): void;
  onSubmit(passcode, cb): void;
}
const UrlAccessCodeChange: React.FC<Props> = (props) => {
  const { shareCard, onSubmit, goBackToEdit } = props;

  const [{ preferredLanguageCode }] = useActiveUser();
  const [, scActions] = useShareCardForActiveProfile();
  const [{ profileId }, profileActions] = useActiveProfile();

  const [state, setState] = React.useState(() => ({
    passcode: generateNewPasscode(4, shareCard),
    saveProgress: false,
    isPasscodeValid: true,
    isEditing: true,
  }));

  const title = shareCard.title.get(preferredLanguageCode);
  const shareCardId = shareCard.id;

  const showInvalidHint = !state.isEditing && !state.isPasscodeValid;
  const disableSubmit = !state.isPasscodeValid;

  const newCodePhrase = ShareCardModel.isEmergencyShareCard(shareCard.definition) ? 'shareCardURLCodeChange.newCode.emergency.message.text' : 'shareCardURLCodeChange.newCode.message.text';

  const onDismiss = () => {
    goBackToEdit();
  };

  return (
    <InfoModal isFullHeight={false} onDismiss={onDismiss}>
      <ContentLoader isContentLoaded={!state.saveProgress} transparentChildren>
        <Spacer size="10">
          <BodyMain>
            <FormattedMessage id="shareCardURLCodeChange.newCode.field.label" />
          </BodyMain>
        </Spacer>

        <Heading>
          {title}
        </Heading>

        <NumberInput
          value={state.passcode}
          onChange={(e) => {
            const { value } = e.target;
            const isValid = isWholeNumber(value) || value === '';
            const passcode = isValid ? value.substr(0, 4) : state.passcode.substr(0, 4);

            setState({
              ...state,
              passcode,
              isPasscodeValid: passcode.length === 4,
            });
          }}
          onBlur={() => { setState({ ...state, isEditing: false }); }}
          onFocus={() => { setState({ ...state, isEditing: true }); }}
          clearValue={() => {
            setState({
              ...state,
              passcode: '',
              isPasscodeValid: false,
            });
          }}
          inputClassName="font-title-2 font-color-profile"
          name="passcode"
          isClearable={true}
          isInvalid={showInvalidHint}
          regularHint={!showInvalidHint && <FormattedMessage id="shareCardURLCodeChange.newCode.field.hint" />}
          invalidHint={showInvalidHint && <FormattedMessage id="shareCardURLCodeChange.newCode.field.hint" />}
          hasInvalidLine={false}
        />

        <Spacer size="24">
          <BodyMain className="font-color-dark-gray">
            <FormattedMessage id={newCodePhrase} />
          </BodyMain>
        </Spacer>

        <Spacer size="8">
          <SecondaryButton
            className="font-color-profile-accent border-color-profile-accent profile-background-color-10-hover"
            onClick={() => {
              if (!state.isPasscodeValid) { return; }

              setState({ ...state, saveProgress: true });
              const profile = profileActions.instantiateProfile();
              scActions.apiUpdateShareCardPasscode(profileId, profile.isDependent(), shareCardId, state.passcode)
                .then(res => {
                  onSubmit(state.passcode, onDismiss);
                });

            }}
            style={{
              opacity: disableSubmit ? 0.6 : 1,
            }}
          >
            <div className="font-title-2">
              <FormattedMessage id="shareCardURLCodeChange.change.button.label" />
            </div>
          </SecondaryButton>
        </Spacer>

        <Spacer size="10">
          <Small>
            <TextLink isUnderlined colorClass="font-color-middle-gray" onClick={onDismiss}>
              <FormattedMessage id="shareCardURLCodeChange.cancel.button.label" />
            </TextLink>
          </Small>
        </Spacer>
      </ContentLoader>
    </InfoModal>
  );
};

export default UrlAccessCodeChange;
